<template>
    <v-card>
        <v-card-title primary-title> {{ formTitle }} </v-card-title>
        <v-card-text>
            <v-form v-model="isFormValid" ref="form" @submit.prevent="submitOrder">
                <v-row>
                    <v-col cols="6">
                        <v-text-field
                            label="Order Name"
                            v-model="name"
                            :rules="orderNameRequired"
                        />
                    </v-col>
                    <v-col cols="6">
                        <v-autocomplete
                            v-model="productionType"
                            :items="productionTypeOptions"
                            label="Production Type"
                            :rules="productionTypeRequired"
                            :disabled="inEditMode"
                            required
                        ></v-autocomplete>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6">
                        <v-autocomplete
                            label="Benchling Folder"
                            v-model="benchlingFolder"
                            :items="benchlingFolderOptions"
                            :item-title="(item) => item.name"
                            :item-value="(item) => item.id"
                            :rules="benchlingFolderRequired"
                            :disabled="inEditMode"
                        />
                    </v-col>
                    <v-col cols="6" v-if="inEditMode">
                        <v-autocomplete label="Status" v-model="status" :items="statusOptions" />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <p>
                            Enter peptide identifiers (e.g. PEP123) from Benchling below, delimited
                            by commas or new lines.
                        </p>
                        <v-textarea
                            label="Peptides"
                            rows="8"
                            variant="outlined"
                            v-model="peptides"
                            :rules="peptidesRequiredAndValid"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6">
                        <v-autocomplete
                            label="Target Purity"
                            v-model="targetPurity"
                            :items="targetPurityOptions"
                            :rules="targetPurityValid"
                            clearable
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="4">
                        <v-autocomplete
                            v-model="quantityNetGross"
                            :items="quantityNetGrossOptions"
                            label="Net vs Gross Weight"
                            clearable
                        />
                    </v-col>
                    <v-col cols="4">
                        <v-text-field
                            label="Desired Quantity"
                            v-model="quantity"
                            type="number"
                            :rules="quantityRequired"
                        />
                    </v-col>
                    <v-col cols="4">
                        <v-autocomplete
                            label="Units"
                            v-model="units"
                            :items="unitsOptions"
                            :rules="unitsRequired"
                            clearable
                        ></v-autocomplete>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6">
                        <v-date-input v-model="requiredByDate" label="Required By"></v-date-input>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="close"> Cancel </v-btn>
            <v-btn color="primary" @click="submitOrder" :disabled="!isFormValid">Submit</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script setup lang="ts">
import {
    ordersAPI,
    ProductionType,
    TargetPurityChemical,
    TargetPurityMicrobial,
    OrderStatus,
    OrderResult,
    SavedOrder,
    NewOrder,
} from "@/services/orders/ordersAPI";
import { withLoadingScreen } from "@/lib/loading.js";
import _ from "lodash";
import { ref, computed, onMounted, watch } from "vue";
import { FolderOption } from "@/services/commonModels";
import { useAuth } from "@/lib/ts/auth";

const auth = useAuth();
const user = computed(() => auth.getAuthUser());

const props = defineProps<{
    benchlingFolderOptions: FolderOption[];
    mode: string;
    order: SavedOrder | null;
}>();

const name = ref("");
const productionType = ref<ProductionType | null>(null);
const benchlingFolder = ref("");
const requiredByDate = ref<Date | null>(null);
const peptides = ref("");
const targetPurity = ref<TargetPurityMicrobial | TargetPurityChemical | null>(null);
const quantityNetGross = ref("");
const quantity = ref("");
const units = ref("");
const status = ref<OrderStatus | null>(null);
const isFormValid = ref(false);

// Options for select fields
const targetPurityOptionsMicrobial = [
    TargetPurityMicrobial.CRUDE,
    TargetPurityMicrobial.PURIFIED,
];
const targetPurityOptionsChemical = [
    TargetPurityChemical.FIFTY_PERCENT,
    TargetPurityChemical.SEVENTY_PERCENT,
    TargetPurityChemical.NINETY_PERCENT,
];
const unitsOptions = ["mg", "ug"];
const productionTypeOptions = [ProductionType.CHEMICAL, ProductionType.MICROBIAL];
const quantityNetGrossOptions = ["net", "gross"];
const statusOptions = [
    OrderStatus.SUBMITTED,
    OrderStatus.IN_PROGRESS,
    OrderStatus.COMPLETED,
    OrderStatus.CANCELED,
];

// Validation rules
const orderNameRequired = [(v: string) => !!v || "Order Name is required"];
const productionTypeRequired = [(v: string) => !!v || "Production Type is required"];
const benchlingFolderRequired = [(v: string) => !!v || "Benchling Folder is required"];
const peptidesRequiredAndValid = [
    (v: string) => !!v || "Peptides are required",
    (v: string) => arePeptidesValid(v) || "Peptides must be in the format PEP123",
];
const targetPurityValid = [
    (v: TargetPurityMicrobial | TargetPurityChemical | null) =>
        isTargetPurityValid(v) ||
        `Invalid target purity for production type ${productionType.value}`,
];
const quantityRequired = [
    (v: string) =>
        !quantityNetGross.value ||
        !!v || "Desired Quantity is required if Net/Gross is selected",
];
const unitsRequired = [
    (v: string) =>
        !quantity.value || !!v || "Units are required if Desired Quantity is provided",
];

const emit = defineEmits<{
    (e: "closeForm"): void;
    (e: "orderSaved", orderResult: any): void;
    (e: "orderError", message: string): void;
}>();

const targetPurityOptions = computed(() => {
    if (productionType.value === ProductionType.MICROBIAL) {
        return targetPurityOptionsMicrobial;
    } else if (productionType.value === ProductionType.CHEMICAL) {
        return targetPurityOptionsChemical;
    } else {
        return [];
    }
});

const formTitle = computed(() => {
    return props.mode === "create"
        ? "Create New Order"
        : `Edit Order ${props.order?.orderCode}`;
});

const inEditMode = computed(() => {
    return props.mode === "edit";
});

onMounted(() => {
    if (inEditMode.value && props.order) {
        name.value = props.order.displayName;
        productionType.value = props.order.productionType;
        benchlingFolder.value = props.order.benchlingFolder;
        requiredByDate.value = props.order.requiredByDate
            ? new Date(props.order.requiredByDate)
            : null;
        targetPurity.value = props.order.targetPurity;
        quantityNetGross.value = props.order.quantityNetGross || "";
        quantity.value = props.order.quantityNeeded?.toString() || "";
        units.value = props.order.quantityNeededUnits || "";
        status.value = props.order.status;
        peptides.value = props.order.peptideCodes.join(", ");
    }
});

function close() {
    reset();
    emit("closeForm");
}

function reset() {
    name.value = "";
    productionType.value = null;
    benchlingFolder.value = "";
    requiredByDate.value = null;
    peptides.value = "";
    targetPurity.value = null;
    quantity.value = "";
    quantityNetGross.value = "";
    units.value = "";
    status.value = null;
}

async function submitOrder() {
    if (!isFormValid.value) {
        return;
    }

    const order = {
        displayName: name.value,
        productionType: productionType.value as ProductionType,
        benchlingFolder: benchlingFolder.value,
        requiredByDate: requiredByDate.value,
        targetPurity: (targetPurity.value as TargetPurityMicrobial | TargetPurityChemical | null) || null,
        quantityNetGross: quantityNetGross.value as "net" | "gross" | null,
        quantityNeeded: quantity.value ? parseFloat(quantity.value) : null,
        quantityNeededUnits: units.value || null,
    };
    const peptideArray = createPeptideArray(peptides.value);

    await withLoadingScreen(async () => {
        try {
            let orderResult: OrderResult;
            if (props.mode === "edit") {
                const updateOrder = order as SavedOrder;
                updateOrder.id = props.order!.id;
                updateOrder.status = status.value as OrderStatus;
                orderResult = await ordersAPI.updatePeptideOrder(updateOrder, peptideArray);
            } else {
                const newOrder = order as NewOrder;
                newOrder.submittedBy = user.value.email;
                orderResult = await ordersAPI.createPeptideOrder({ ...newOrder, peptides: peptideArray });
            }
            if (orderResult.order) {
                emit("orderSaved", orderResult);
            }
        } catch (error) {
            const message = _.get(error, "response.data.error") || "An error occurred saving the order."
            emit("orderError", message);
        }
    });
}

function createPeptideArray(peptides: string): string[] {
    return peptides.split(/[\s,]+/).filter((peptide) => peptide.length > 0);
}

function arePeptidesValid(peptides: string): boolean {
    // Peptides must be in the format of PEP followed by 3–6 digits
    // Allow for trailing commas, new lines, or spaces
    const peptideRegex = /^PEP\d{3,6}$/;
    const peptideArray = createPeptideArray(peptides);
    return peptideArray.every((peptide) => peptideRegex.test(peptide));
}

function isTargetPurityValid(targetPurity: TargetPurityMicrobial | TargetPurityChemical | null): boolean {
    if (!targetPurity) {
        return true;
    }
    if (productionType.value === ProductionType.MICROBIAL) {
        return targetPurityOptionsMicrobial.includes(targetPurity as TargetPurityMicrobial);
    } else if (productionType.value === ProductionType.CHEMICAL) {
        return targetPurityOptionsChemical.includes(targetPurity as TargetPurityChemical);
    }
    return true;
}

watch(productionType, () => {
    if (!isTargetPurityValid(targetPurity.value)) {
        targetPurity.value = null;
    }
});
</script>
